import { useEffect, useRef, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { useAuth, type CurrentUser } from '../../auth/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { FormField } from '../../components/ui/FormField';

export function EnrollmentForm({ challengeToken, onEnrolled }: {
  challengeToken: string;
  onEnrolled: (recoveryCodes: string[], user: CurrentUser) => void;
}) {
  const { enrollOtp, confirmOtp } = useAuth();
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;
    enrollOtp(challengeToken)
      .then((r) => {
        if (cancelled) return;
        setSecret(r.secret);
        setOtpauthUri(r.otpauthUri);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Enrollment failed'));
    return () => {
      cancelled = true;
    };
  }, [challengeToken, enrollOtp]);

  useEffect(() => {
    if (canvasRef.current && otpauthUri) {
      void QRCode.toCanvas(canvasRef.current, otpauthUri, { width: 192 });
    }
  }, [otpauthUri]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const { user, recoveryCodes } = await confirmOtp(challengeToken, code.trim());
      onEnrolled(recoveryCodes, user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setPending(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="w-full max-w-sm rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-6 shadow-sm flex flex-col gap-4"
      noValidate
    >
      <header className="mb-2">
        <h1 className="text-xl font-semibold">Set up two-factor authentication</h1>
        <p className="text-sm text-[var(--co-muted)]">
          Scan the QR code with an authenticator app (Google Authenticator, 1Password…), then enter the 6-digit code to confirm.
        </p>
      </header>
      <canvas ref={canvasRef} className="mx-auto" aria-label="TOTP QR code" />
      {secret && (
        <p className="text-xs text-[var(--co-muted)] break-all">
          Can’t scan? Enter this key manually: <code>{secret}</code>
        </p>
      )}
      <FormField label="Authenticator code" htmlFor="enroll-code" error={error ?? undefined}>
        <Input id="enroll-code" autoComplete="one-time-code" inputMode="numeric" required
               value={code} onChange={(e) => setCode(e.target.value)} />
      </FormField>
      <Button type="submit" disabled={pending || !secret}>
        {pending ? 'Confirming…' : 'Confirm'}
      </Button>
    </form>
  );
}
